<template>
    <div v-if="field" class="field">
        <div class="json-controls">
            <b-button-group size="sm">
                <b-button 
                    :variant="viewMode === 'viewer' ? 'primary' : 'outline-secondary'" 
                    @click="viewMode = 'viewer'"
                >
                    <b-icon icon="diagram3" class="mr-1"></b-icon>
                    Viewer
                </b-button>
                <b-button 
                    :variant="viewMode === 'code' ? 'primary' : 'outline-secondary'" 
                    @click="viewMode = 'code'"
                >
                    <b-icon icon="code-slash" class="mr-1"></b-icon>
                    Code
                </b-button>
            </b-button-group>

            <div v-if="viewMode === 'viewer'" class="search-box">
                <b-input-group size="sm">
                    <b-form-input 
                        v-model="searchQuery" 
                        placeholder="Search keys or values..."
                        @input="onSearch"
                    ></b-form-input>
                    <b-input-group-append>
                        <b-button variant="outline-secondary" @click="searchQuery = ''" v-if="searchQuery">
                            <b-icon icon="x"></b-icon>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </div>
        </div>

        <div v-show="viewMode === 'viewer'" class="json-viewer-container" ref="jsonViewerContainer">
            <div v-if="!value || (typeof value === 'object' && Object.keys(value).length === 0)" class="empty-state">
                <b-icon icon="code-square" font-scale="2" class="text-muted mb-2"></b-icon>
                <p class="text-muted">No data to display</p>
            </div>
            <JsonTreeView 
                v-else
                ref="jsonTreeView"
                :data="value" 
                :maxDepth="3"
                :searchQuery="searchQuery"
                :readOnly="field.protected"
                @update="handleJsonUpdate"
                @first-match="scrollToFirstMatch"
            />
        </div>

        <div v-show="viewMode === 'code'" class="code-editor-container">
            <CodeEditor v-model="content" :withButton="false" :readOnly="field.protected"></CodeEditor>
        </div>
        
        <p class="errorMsg" v-if="errorMsg"> {{ errorMsg }} </p>
    </div>
</template>

<style scoped>
div.field {
    padding: 10px;
}

.json-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    gap: 10px;
}

.search-box {
    flex: 0 0 300px;
}

.json-viewer-container {
    border: 1px solid var(--border-separator, #dee2e6);
    border-radius: 4px;
    padding: 15px;
    background: var(--bg-card, #ffffff);
    max-height: 600px;
    overflow-y: auto;
}

.code-editor-container {
    border-radius: 4px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    text-align: center;
}

.errorMsg {
    color: red;
    font-size: 12px;
    margin-top: 10px;
}

.field-datajson-hide {
    position: absolute;
    width: 100%;
    height: 100%;
    background: rgba(255,255,255,0.1);
    display: flex;
    cursor: not-allowed;
}

.show-data {
    position: relative;
}
</style>

<script>
import _ from 'lodash'

export default {
    name: "IdentityFieldDataJson",
    components: {
        CodeEditor: () => import('../../../form/Editor/Code'),
        JsonTreeView: () => import('../../../elements/JsonTreeView')
    },
    props: {
        field: {
            type: Object
        },
        value: {
            type: [Object, Array]
        },
        store: {
            type: Object
        },
        context: {
            type: Object
        }
    },
    data: function() {
        return {
            errors: [],
            httpErrors: [],
            model: {},
            editor: false,
            showCode: false,
            errorMsg: '',
            errorModal: {
                show: false,
                msg: null
            },
            viewMode: 'viewer',
            searchQuery: ''
        }
    },
    computed: {
        content: {
            get() {
                try {
                    this.errorMsg = '' 
                    return JSON.stringify(this.value || {}, null, 2)
                } catch(err) {
                    this.errorMsg = err
                }
            },

            set(val) {
                try {
                    this.errorMsg = '' 
                    this.$emit('input', JSON.parse(val))
                } catch(err) {
                    this.errorMsg = err
                }
            }
        }
    },
    methods: {
        onSearch: _.debounce(function() {
            // Search is handled by the JsonTreeView component
        }, 300),

        handleJsonUpdate(newValue) {
            if (!this.field.protected) {
                this.$emit('input', newValue)
            }
        },

        scrollToFirstMatch(element) {
            this.$nextTick(() => {
                if (element && this.$refs.jsonViewerContainer) {
                    const container = this.$refs.jsonViewerContainer
                    const elementTop = element.offsetTop
                    const containerTop = container.scrollTop
                    const containerHeight = container.clientHeight
                    
                    // Check if element is not in view
                    if (elementTop < containerTop || elementTop > containerTop + containerHeight) {
                        // Scroll to element with some offset for better visibility
                        container.scrollTo({
                            top: elementTop - 100,
                            behavior: 'smooth'
                        })
                    }
                }
            })
        }
    },

    created() {
    }

}
</script>